/**
 * Google sign-in via the Cognito hosted UI (authorization code + PKCE).
 *
 * The app client has no secret (public SPA), so the code flow is secured with
 * PKCE rather than a client secret. We drive the hosted UI directly instead of
 * pulling in Amplify: the only thing we need from it is a code-for-token
 * exchange, and Amplify would duplicate the session handling that auth.ts
 * already owns.
 *
 * After the exchange, tokens are written into the exact localStorage keys
 * `amazon-cognito-identity-js` reads. That keeps a single session mechanism —
 * `restoreSession()`, `signOut()` and refresh all keep working, with no
 * awareness that this user arrived via Google.
 */

const hostedDomain = import.meta.env.VITE_COGNITO_HOSTED_DOMAIN ?? "";
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID ?? "";

/**
 * Google sign-in is shown only when the pool really has the IdP configured.
 * The hosted domain exists in every environment, so its presence alone would
 * render a button that dead-ends on Cognito's "identity provider not
 * supported" error.
 */
export const googleSignInEnabled =
  import.meta.env.VITE_GOOGLE_SIGN_IN === "true" &&
  Boolean(hostedDomain) &&
  Boolean(clientId);

const VERIFIER_KEY = "sw.oauth.pkceVerifier";

function redirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

function base64UrlEncode(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return base64UrlEncode(new Uint8Array(digest));
}

/** Send the browser to Google via the Cognito hosted UI. */
export async function startGoogleSignIn(): Promise<void> {
  const verifier = randomVerifier();
  // Survives the round trip to Google; required to redeem the code.
  sessionStorage.setItem(VERIFIER_KEY, verifier);

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: "code",
    scope: "email openid profile",
    redirect_uri: redirectUri(),
    identity_provider: "Google",
    code_challenge: await challengeFor(verifier),
    code_challenge_method: "S256",
  });
  window.location.assign(`${hostedDomain}/oauth2/authorize?${params}`);
}

interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

/**
 * Write tokens where `amazon-cognito-identity-js` expects to find them, so the
 * rest of the app treats a federated session like any other.
 */
function storeCognitoSession(tokens: TokenResponse): void {
  const claims = JSON.parse(
    atob(tokens.id_token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
  ) as { "cognito:username"?: string; sub: string };
  const username = claims["cognito:username"] ?? claims.sub;
  const prefix = `CognitoIdentityServiceProvider.${clientId}`;

  localStorage.setItem(`${prefix}.LastAuthUser`, username);
  localStorage.setItem(`${prefix}.${username}.idToken`, tokens.id_token);
  localStorage.setItem(`${prefix}.${username}.accessToken`, tokens.access_token);
  if (tokens.refresh_token) {
    localStorage.setItem(`${prefix}.${username}.refreshToken`, tokens.refresh_token);
  }
  localStorage.setItem(`${prefix}.${username}.clockDrift`, "0");
}

/** Redeem the `?code=` from the hosted-UI redirect and establish a session. */
export async function completeGoogleSignIn(code: string): Promise<void> {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  if (!verifier) {
    throw new Error(
      "This sign-in couldn’t be completed in this browser. Please try again.",
    );
  }

  const res = await fetch(`${hostedDomain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: clientId,
      code,
      redirect_uri: redirectUri(),
      code_verifier: verifier,
    }),
  });

  if (!res.ok) {
    // Cognito returns {error: "invalid_grant"} and friends; surface something
    // a user can act on rather than the raw code.
    throw new Error("Google sign-in failed. Please try again.");
  }
  storeCognitoSession((await res.json()) as TokenResponse);
}
